
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Shield, ShieldCheck, ShieldAlert } from 'lucide-react';
import { UserWithMembership } from '@/hooks/useUsers';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';
import UserCard from './UserCard';
import UserActions from './UserActions';

interface UsersListProps {
  users: UserWithMembership[];
  onEdit: (user: UserWithMembership) => void;
  onDelete: (user: UserWithMembership) => void;
  onResetPassword: (user: UserWithMembership) => void;
  onToggleStatus: (user: UserWithMembership) => void;
  isAdmin: boolean;
  isTogglingStatus?: boolean;
}

const UsersList: React.FC<UsersListProps> = ({
  users,
  onEdit,
  onDelete,
  onResetPassword,
  onToggleStatus,
  isAdmin,
  isTogglingStatus = false,
}) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <ShieldAlert className="h-4 w-4" />;
      case 'manager':
        return <ShieldCheck className="h-4 w-4" />;
      default:
        return <Shield className="h-4 w-4" />;
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin':
        return 'destructive';
      case 'manager':
        return 'default';
      default:
        return 'secondary';
    }
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'admin':
        return t('users.admin');
      case 'manager':
        return t('users.manager');
      case 'staff':
        return t('users.user');
      default:
        return t('users.user');
    }
  };

  if (isMobile) {
    return (
      <div className="space-y-4">
        {users.map((user) => (
          <UserCard
            key={user.id}
            user={user}
            onEdit={onEdit}
            onDelete={onDelete}
            onResetPassword={onResetPassword}
            onToggleStatus={onToggleStatus}
            canDelete={isAdmin}
            isTogglingStatus={isTogglingStatus}
          />
        ))}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('users.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('users.fullName')}</TableHead>
              <TableHead>{t('users.role')}</TableHead>
              <TableHead>{t('users.lastLogin')}</TableHead>
              <TableHead>{t('users.created')}</TableHead>
              <TableHead className="text-center">{t('users.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{user.full_name}</TableCell>
                <TableCell>
                  <Badge
                    variant={getRoleBadgeVariant(user.role ?? 'staff')}
                    className="flex items-center gap-1 w-fit"
                  >
                    {getRoleIcon(user.role ?? 'staff')}
                    {getRoleDisplayName(user.role ?? 'staff')}
                  </Badge>
                </TableCell>
                <TableCell>
                  {user.last_login_at
                    ? new Date(user.last_login_at).toLocaleDateString()
                    : t('users.never')}
                </TableCell>
                <TableCell>
                  {new Date(user.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center gap-2 justify-end">
                    <UserActions
                      user={user}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onResetPassword={onResetPassword}
                      onToggleStatus={onToggleStatus}
                      canDelete={isAdmin}
                      isTogglingStatus={isTogglingStatus}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default UsersList;
